// One dialer everywhere (CU-869fmbgfj): pure helpers (no React), so Jest loads
// them without the components barrel. Which way this user calls (the backend's
// `voice-calls/dialer/` answer, contract ~/pm/contracts/V2-3.md v1), the
// widget's pre-fill text, and the event other screens use to open the widget.
import { CONNECTION, deskLabel, isDiallable } from './outboundModel';

export const ROUTE = Object.freeze({ ELEVATE: 'elevate', TWILIO: 'twilio', NONE: 'none' });

// Any screen (or another branch's code) can open the one dialer without
// importing the widget: window.dispatchEvent(new CustomEvent(OPEN_DIALER_EVENT,
// { cancelable: true, detail: { to, leadId, callId, provider } })). The widget cancels
// the event when it opens, so the sender can tell nobody was listening.
export const OPEN_DIALER_EVENT = 'dialer:open';

export const requestDialer = (detail) => {
    if (typeof window === 'undefined') return false;
    const event = new CustomEvent(OPEN_DIALER_EVENT, { cancelable: true, detail: detail || {} });
    return !window.dispatchEvent(event);
};

export const EMPTY_PREFILL = Object.freeze({ number: '', number_display: '', lead: null, call: null });

// Query for GET voice-calls/dialer/. Only voice rows are calls the backend can
// look up (`voice:9` or 9); Twilio / WhatsApp Call Logs rows pre-fill from the
// number alone.
export const dialerQuery = ({ leadId, callId, to } = {}) => {
    const query = {};
    if (leadId != null && /^\d+$/.test(String(leadId))) query.lead_id = String(leadId);
    const ref = callId != null ? String(callId) : '';
    if (/^(voice:)?\d+$/.test(ref)) query.call = ref;
    if (isDiallable(to)) query.number = to.trim();
    return query;
};

const ROUTES = new Set(Object.values(ROUTE));

// No answer, an old backend without the endpoint, or a refused query: keep
// today's behaviour (Elevate callers get their desk, Twilio users their picker)
// with no pre-fill beyond what the screen passed in.
export const readDialer = (response, { canCallElevate = false, canUseTwilio = false } = {}) => {
    const data = response?.ok ? response.data : null;
    if (data && ROUTES.has(data.route)) {
        return {
            route: data.route,
            elevate: data.elevate || null,
            twilio: data.twilio || null,
            prefill: { ...EMPTY_PREFILL, ...(data.prefill || {}) },
            // Why there is no route (CU-869fn2wd3); '' from an older backend.
            reason: typeof data.reason === 'string' ? data.reason : '',
            fromServer: true,
        };
    }
    let route = ROUTE.NONE;
    if (canCallElevate) route = ROUTE.ELEVATE;
    else if (canUseTwilio) route = ROUTE.TWILIO;
    return { route, elevate: null, twilio: null, prefill: { ...EMPTY_PREFILL }, reason: '', fromServer: false };
};

export const DESK_STATE = Object.freeze({ READY: 'ready', PICK: 'pick', HANDSET: 'handset' });

// What the Elevate part of the widget says. It follows this computer's desk
// (the one Call will ring, OutboundCallProvider), not the server's guess.
export const deskStatus = (desk) => {
    if (!desk) return { state: DESK_STATE.PICK, text: 'Pick which desk this computer is to call from the CRM.' };
    const name = deskLabel(desk);
    if (desk.connection_state === CONNECTION.CONNECTED) {
        return { state: DESK_STATE.READY, text: `Calling from ${name} (your desk)` };
    }
    const why = desk.connection_state === CONNECTION.NEEDS_RECONNECTING
        ? `${name} needs reconnecting`
        : `${name} isn't connected to the CRM yet`;
    return { state: DESK_STATE.HANDSET, text: `Dial on your handset: ${why}.` };
};

// A desk caller in a practice with no handsets set up yet: there is nothing to pick.
export const NO_HANDSETS_TEXT = Object.freeze({
    admin: 'No handsets are set up for calling from the CRM yet. Set them up in Settings → Phone system → Desks.',
    staff: 'No handsets are set up for calling from the CRM yet. Ask a practice admin to set them up.',
});

const UK_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' });
const UK_DATE_TIME = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London',
});

const fmt = (formatter, iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '' : formatter.format(date);
};

// The widget's header and detail lines, built from the backend pre-fill plus
// whatever the screen passed (number typed in, lead from the table row).
export const prefillView = (prefill, { to = '', lead = null } = {}) => {
    const p = prefill || EMPTY_PREFILL;
    const leadName = p.lead?.name || [lead?.first_name, lead?.last_name].filter(Boolean).join(' ').trim();
    const number = p.number_display || p.number || to || '';
    const details = [];
    if (leadName && number) details.push(number);
    // Labelled, so a treatment called "Emergency" doesn't read like an alert.
    if (p.lead?.treatment) details.push(`Treatment: ${p.lead.treatment}`);
    if (p.lead?.owner) details.push(`Owner: ${p.lead.owner}`);
    const notes = [];
    if (p.call?.label) notes.push(p.call.label);
    if (p.call?.line) notes.push(`Line: ${p.call.line}`);
    const next = fmt(UK_DATE_TIME, p.lead?.next_appointment_at);
    if (next) notes.push(`Next appointment ${next}`);
    const last = fmt(UK_DATE, p.lead?.last_contact_at);
    if (last) notes.push(`Last contact ${last}`);
    let title = 'New call';
    if (leadName) title = `Call ${leadName}`;
    else if (number) title = `Call ${number}`;
    return {
        title,
        details,
        notes,
        archived: Boolean(p.lead?.is_archived),
    };
};

// The number Call dials: the backend's normalised one when it has it (E.164),
// else what the user typed.
export const dialNumber = (prefill, typed) => {
    const typedClean = (typed || '').trim();
    if (typedClean && typedClean !== prefill?.number_display) return typedClean;
    return prefill?.number || typedClean;
};

// The route the widget actually uses. The server decides Elevate vs Twilio,
// but this screen may not be able to use Elevate (kill switch, desk list not
// loaded or failed), and a desk that can't be rung shouldn't hide working
// Twilio numbers from someone allowed to use them.
export const effectiveRoute = ({ route, canCallElevate = false, canUseTwilio = false, deskState = null }) => {
    if (route === ROUTE.ELEVATE) {
        if (canCallElevate && deskState !== DESK_STATE.HANDSET) return ROUTE.ELEVATE;
        if (canUseTwilio) return ROUTE.TWILIO;
        return canCallElevate ? ROUTE.ELEVATE : ROUTE.NONE;
    }
    if (route === ROUTE.TWILIO) return canUseTwilio ? ROUTE.TWILIO : ROUTE.NONE;
    return ROUTE.NONE;
};

// ── Call back on the channel they called in on (CU-869fn2wcy) ───────────────
// The ways this box can return a call. WhatsApp calls are placed from the person's WhatsApp chat
// (the call button there), so choosing it here opens that chat instead of dialling.
export const CHANNEL = Object.freeze({ ELEVATE: 'elevate', TWILIO: 'twilio', WHATSAPP: 'whatsapp' });
export const CHANNEL_LABEL = Object.freeze({
    [CHANNEL.ELEVATE]: 'Desk phone',
    [CHANNEL.TWILIO]: 'Browser (Twilio)',
    [CHANNEL.WHATSAPP]: 'WhatsApp',
});
const CHANNEL_ORDER = [CHANNEL.ELEVATE, CHANNEL.TWILIO, CHANNEL.WHATSAPP];
const CHANNELS = new Set(CHANNEL_ORDER);

// The channel a call came in on. The row id says it (`voice:9` is the phone system, `twilio:12`,
// `whatsapp:3`); a row's `provider` is the fallback. Queue rows default `provider` to "twilio" for
// every phone call, so the id wins. Null when neither says.
export const callChannel = ({ callId, provider } = {}) => {
    const prefix = /^([a-z]+):/.exec(String(callId ?? ''))?.[1];
    if (prefix === 'voice') return CHANNEL.ELEVATE;
    if (prefix === CHANNEL.TWILIO || prefix === CHANNEL.WHATSAPP) return prefix;
    const p = String(provider || '').toLowerCase();
    return CHANNELS.has(p) ? p : null;
};

// The channels this user can use right now, in switch order. Desk phone only when the server's
// route is the desk (it checks the practice's connection and that this user may dial), browser
// only with a Twilio number, WhatsApp only with the practice's WhatsApp calling and someone to open.
export const usableChannels = ({
    route, canCallElevate = false, canUseTwilio = false, twilioNumbers = 0, canWhatsappCall = false, hasPerson = false,
} = {}) => CHANNEL_ORDER.filter((c) => {
    if (c === CHANNEL.ELEVATE) return canCallElevate && route === ROUTE.ELEVATE;
    if (c === CHANNEL.TWILIO) return canUseTwilio && twilioNumbers > 0;
    return canWhatsappCall && hasPerson;
});

// Which channel the box shows: the user's pick, else the channel they called in on, else the
// server's route (`fallback`, effectiveRoute's answer; ROUTE.NONE keeps "isn't set up").
// A desk that can't be rung isn't the default while the browser works (effectiveRoute's rule).
// `why` says which of the three it was.
export const chooseChannel = ({ picked = null, origin = null, fallback = ROUTE.NONE, usable = [], deskState = null } = {}) => {
    if (picked && usable.includes(picked)) return { channel: picked, why: 'picked' };
    const deskOnHandset = origin === CHANNEL.ELEVATE && deskState === DESK_STATE.HANDSET && usable.includes(CHANNEL.TWILIO);
    if (origin && usable.includes(origin) && !deskOnHandset) return { channel: origin, why: 'origin' };
    return { channel: fallback === ROUTE.NONE ? null : fallback, why: 'server' };
};

// Show the switch only when it offers a real choice: two or more usable channels, or one usable
// channel the box isn't on (the server found no route, but WhatsApp works).
export const showChannelSwitch = (usable, channel) => usable.length > 1 || (usable.length === 1 && usable[0] !== channel);

// Where "WhatsApp" goes: the lead's WhatsApp chat (its All communications tab opens on WhatsApp),
// else the WhatsApp page. An archived lead's page can't be opened, so that goes to WhatsApp too.
export const whatsappTarget = ({ practiceId, leadId = null, archived = false } = {}) => {
    if (leadId != null && /^\d+$/.test(String(leadId)) && !archived) {
        return {
            path: `/dashboard/practice/${practiceId}/lead-detail/${leadId}?tab=${encodeURIComponent('All communications')}`,
            state: { goToWhatsappTab: true },
            text: 'WhatsApp calls are made from their WhatsApp chat. Open it and use the call button there.',
            button: 'Open WhatsApp chat',
        };
    }
    return {
        path: `/dashboard/practice/${practiceId}/whatsapp`,
        state: null,
        text: 'WhatsApp calls are made from the WhatsApp page. Open their chat there and use the call button.',
        button: 'Open WhatsApp',
    };
};

// lead_id sent with the dial: the screen's lead, else the one the server
// found for the Call Logs row, but only while the number is the one we pre-filled.
export const dialLeadId = (prefill, activeLeadId, number) => {
    if (activeLeadId != null) return activeLeadId;
    const serverLead = prefill?.lead?.id ?? null;
    return serverLead != null && number && number === prefill?.number ? serverLead : null;
};

// ── "Calling isn't set up for you" (CU-869fmc59c R9) ────────────────────────
// The dialer opens for anyone allowed to call, but the server may find no way to ring for this
// user (no desk chosen, no Twilio number). Give each person their way forward.
export const NO_ROUTE_HELP = Object.freeze({ CHOOSE_DESK: 'choose_desk', SET_UP_DESKS: 'set_up_desks', ASK_ADMIN: 'ask_admin' });
export const noRouteHelp = ({ canCallElevate = false, isVoiceAdmin = false } = {}) => {
    if (canCallElevate) return NO_ROUTE_HELP.CHOOSE_DESK;
    return isVoiceAdmin ? NO_ROUTE_HELP.SET_UP_DESKS : NO_ROUTE_HELP.ASK_ADMIN;
};
// Query on the Integrations page that opens the phone system's Desks tab.
export const DESKS_TAB_QUERY = 'voiceTab=desks';

// UK number the dialer's field shows as an example, for every route.
export const DIALER_PLACEHOLDER = '07700 900456';

// The lead whose page the user is on (/dashboard/practice/:practiceId/lead-detail/:id/...), else null.
// The floating call button passes it, so the server fills in that lead's number.
export const leadIdFromPath = (pathname) => {
    const match = /\/lead-detail\/([^/]+)/.exec(String(pathname || ''));
    return match && /^\d+$/.test(match[1]) ? Number(match[1]) : null;
};
